import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, pairwise, takeUntil } from 'rxjs/operators';
import { isEmpty, omit } from 'lodash';
import { environment } from 'environments/environment';
import { GlobalFuntions } from 'app/_helpers';

export interface AuditPayload {
    changed_attributes: any;
    curr_val_changed_attr: any;
}

export interface AuditTrackingResult {
    changedProperties: any;
    currentValues: any;
}

/**
 * Timeline Event Interface - for functional activities
 * Examples: Adding/removing clients, executing digital services, adding objects
 */
export interface TimelineEvent {
    description: string;
    metadata?: any; // Additional context (client_id, service_name, object_type, etc.)
}

@Injectable({
    providedIn: 'root'
})
export class AuditService {

    constructor(
        private http: HttpClient,
        private _globalFunction: GlobalFuntions,
    ) { }

    /**
     * Get audit history for a specific entity
     * @param id - Entity ID
     * @param name - Model name (e.g., 'commercial_property', 'dossier', 'client')
     * @param resId - Optional residential ID for tracking
     */
    getAuditHistory(id: any, name: any, resId: string = ''): Observable<any> {
        let params = `id=${id}&modelName=${name}&expand=changedAttributesUpdatedByUser`;
        if (resId) {
            params += `&res_id=${resId}`;
        }
        return this.http.get(environment.yiiUrl + `history/get-histories?${params}`, { observe: 'response' as 'body' });
    }

    /**
     * GENERALIZED: Save history for ANY model/collection with support for Timeline and Audit types
     * Use this single method for all entities: commercial_property, dossier, client, etc.
     * 
     * @param id - The entity ID
     * @param model - The model name (e.g., 'commercial_property', 'dossier', 'client')
     * @param view_url - The action/view being performed (e.g., 'edit-commercial', 'view-dossier')
     * @param data - Payload containing changed_attributes and curr_val_changed_attr
     */
    saveHistory(
        id: string,
        model: string,
        view_url: string,
        data: AuditPayload,
    ): Observable<any> {
        const params = new URLSearchParams();
        params.set('id', id);
        params.set('model', model);
        params.set('view_url', view_url);

        return this.http.post<any>(
            `${environment.yiiUrl}history/save-history?${params.toString()}`,
            data
        );
    }

    /**
     * Track form changes with ACCUMULATION across multiple changes
     * Accumulates ALL changes into a single object
     * RECOMMENDED for most use cases (commercial, dossier, client, etc.)
     * 
     * @param form - FormGroup to track
     * @param destroy$ - Subject to unsubscribe on component destroy
     * @param debounceMs - Debounce time in milliseconds (default: 900)
     * @param excludeKeys - Control names to leave out of the audit diff, e.g. relationship
     *                      fields (clients, property_id) that already have their own
     *                      purpose-built timeline logging server-side.
     * @returns Observable emitting accumulated AuditTrackingResult
     */
    trackFormChangesAccumulated(
        form: FormGroup,
        destroy$: Subject<void>,
        debounceMs: number = 900,
        excludeKeys: string[] = []
    ): Observable<AuditTrackingResult> {
        return new Observable(observer => {
            let accumulatedChanges: any = {};
            let accumulatedCurrentValues: any = {};

            form.valueChanges
                .pipe(
                    debounceTime(debounceMs),
                    distinctUntilChanged(),
                    pairwise(),
                    takeUntil(destroy$)
                )
                .subscribe(([oldValue, newValue]) => {
                    const diff = omit(this._globalFunction.difference(oldValue, newValue), excludeKeys);

                    // Accumulate changes (a single emission can contain more than one
                    // changed control, e.g. financed_amount + own_funds updating together)
                    if (!isEmpty(diff)) {
                        accumulatedChanges = {
                            ...accumulatedChanges,
                            ...diff,
                        };

                        Object.keys(diff).forEach((changedKey) => {
                            const changedValue = newValue[changedKey];
                            if (changedValue !== undefined) {
                                accumulatedCurrentValues = {
                                    ...accumulatedCurrentValues,
                                    [changedKey]: changedValue,
                                };
                            }
                        });
                    }

                    const result: AuditTrackingResult = {
                        changedProperties: accumulatedChanges,
                        currentValues: accumulatedCurrentValues
                    };

                    observer.next(result);
                });
        });
    }

    /**
     * Check if there are any changes to audit
     */
    hasChanges(changedProperties: any): boolean {
        return !isEmpty(changedProperties);
    }

    /**
     * Prepare audit payload for saveHistory
     */
    prepareAuditPayload(
        changedProperties: any,
        currentValues: any
    ): AuditPayload {
        return {
            changed_attributes: changedProperties,
            curr_val_changed_attr: currentValues
        };
    }

    /**
     * Reset changed properties after successful save
     */
    resetChangedProperties(): any {
        return {};
    }
}